// React
import React from "react"

// Styles
import "./index.css"

// Components
import { AnalyticsLayout, CompanyLayout, IframeLayout, Layout } from "./components"

// Configs
import { menu, setupMenu } from "./configs"

// Contexts
import { AuthProvider, CompanyProvider, CompanyType, ThemeProvider } from "contexts"

// Pages
import Error from "pages/Error"
import Subscribe from "pages/Subscribe"
import Login from "./pages/Login"

// Routes
import { accountsRoutes } from "routes/accountsRoutes"
import { analyticsRoutes } from "routes/analyticsRoutes"
import { cardsRoutes } from "routes/cardsRoutes"
import { financeRoutes } from "routes/financeRoutes"
import { inventoryRoutes } from "routes/inventoryRoutes"
import { purchaseRoutes } from "routes/purchaseRoutes"
import { salesRoutes } from "routes/salesRoutes"
import { mainRoute } from "./routes/mainRoute"
import { setupRoute } from "./routes/setupRoute"
import { tasksRoutes } from "routes/tasksRoutes"
import { iframeRoutes } from "routes/iframeRoutes"
import { projectRoutes } from "routes/projectRoutes"

// Third-Party Libraries
import ReactDOM from "react-dom/client"
import { Toaster } from "react-hot-toast"
import { createBrowserRouter, RouterProvider } from "react-router-dom"
import reportWebVitals from "./reportWebVitals"

// Types
import type { RouteType, SuccessFetch } from "types"

// Utils
import { getBasenamePath, getValidationSubdomain, routeMapper } from "utils"
import { assetManagementRoutes } from "routes/assetManagementRoutes"

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement)

// Vars
const routeModuleList: { [key: string]: RouteType[] } = {
  ANALYTICS: [],
  ACCOUNTS: accountsRoutes,
  ASSET_MANAGEMENT: assetManagementRoutes,
  FINANCE: financeRoutes,
  SALES: salesRoutes,
  PURCHASES: purchaseRoutes,
  INVENTORY: inventoryRoutes,
  CARDS: cardsRoutes,
  TASKS: tasksRoutes,
  PROJECT:projectRoutes,
}

const { basename, companyName } = getBasenamePath()

const router = createBrowserRouter(
  [
    {
      path: "",
      errorElement: <Error />,
      element: <CompanyLayout />,
      loader: async () => {
        return getValidationSubdomain({ subdomain: companyName })
          .then((res: SuccessFetch<{ payload: CompanyType }>) => res.data.payload)
          .catch(() => null)
      },
      children: [
        {
          path: "/login",
          element: <Login />,
          loader: () =>
            getValidationSubdomain({ subdomain: companyName })
              .then((res) => res?.data)
              .catch(() => null)
        },
        {
          path: "/subscribe",
          element: <Subscribe />
        },
        {
          path: "/setup",
          children: routeMapper(setupRoute),
          element: <Layout outletClass="py-5" module_menu={setupMenu} />
        },
        {
          path: "/iframe",
          children: iframeRoutes,
          element: <IframeLayout />
        },
        {
          path: "/",
          element: <Layout module_menu={menu} routeList={routeModuleList} />,
          children: [
            ...mainRoute,
            {
              path: "/analytics",
              element: <AnalyticsLayout />,
              children: routeMapper(analyticsRoutes)
            }
          ]
        }
      ]
    }
  ],
  { basename }
)

root.render(
  <React.StrictMode>
    <ThemeProvider>
      <AuthProvider>
        <CompanyProvider>
          <RouterProvider router={router} />
          <Toaster />
        </CompanyProvider>
      </AuthProvider>
    </ThemeProvider>
  </React.StrictMode>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
